import React from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import { useAuthCircuit } from 'src/hooks/useAuthCircuit';
import { openCircuit, resetCircuit } from 'src/utils/authCircuitBreaker';

// Intentionally does NOT use useTranslate: the modal is mounted above the
// MbmProvider so it is still rendered during the auth-loop condition where
// AuthProvider is stuck on its spinner.
export const ReAuthModal = () => {
    const circuit = useAuthCircuit();

    const handleSignInAgain = async () => {
        try {
            await signOut();
        } catch (err) {
            // signOut may fail if the Cognito session is already invalid;
            // we still want to drive the user back through the hosted UI.
            console.error('signOut during re-auth failed', err);
        }
        try {
            // Await so navigation starts in a well-defined order after
            // signOut resolves; without await the redirect call races the
            // sign-out teardown.
            await signInWithRedirect({
                provider: {
                    custom: 'FederateSignIn',
                },
            });
            // Only reset the circuit once the redirect has been initiated.
            // Resetting before this point would leave the user with no modal
            // if signInWithRedirect throws (e.g. Amplify config missing),
            // and no way to try again.
            resetCircuit();
        } catch (err) {
            console.error('signInWithRedirect during re-auth failed', err);
            // Re-open the circuit so the modal stays visible and the user
            // can retry. Preserve the original reason if we still have one.
            openCircuit(circuit.reason ?? 'consecutive-auth-failures');
        }
    };

    return (
        <Modal
            visible={circuit.open}
            // No dismiss path — the only exits are "Sign in again" or the
            // 10-minute auto-reset. onDismiss intentionally omitted so the
            // Cloudscape close affordance is not rendered.
            header="Session expired"
            data-testid="reauth-modal"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            variant="primary"
                            data-testid="reauth-modal-signin-button"
                            onClick={handleSignInAgain}
                        >
                            Sign in again
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            Your session has expired. Please sign in again to continue.
        </Modal>
    );
};

export default ReAuthModal;
